import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  List,
  Select,
  Skeleton,
  Tag,
  message,
  Modal,
  Popconfirm,
  Space,
} from "antd";
import {
  Tags,
  PlusCircle,
  RefreshCw,
  Edit3,
  Trash2,
  ShieldCheck,
  RotateCcw,
} from "lucide-react";
import communityAPI from "../../../api/community";
import { useCommunityAuth } from "../../../contexts/CommunityAuthContext";
const normalizeList = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && Array.isArray(payload.results)) {
    return payload.results;
  }
  return [];
};

const slugifyValue = (value) => {
  if (!value) {
    return "";
  }
  return value
    .toString()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 150);
};

const buildSlug = (name, slugValue, fallback = "") => {
  const explicit = slugifyValue(slugValue);
  if (explicit) {
    return explicit;
  }
  const fromName = slugifyValue(name);
  if (fromName) {
    return fromName;
  }
  const fallbackSlug = slugifyValue(fallback);
  if (fallbackSlug) {
    return fallbackSlug;
  }
  const randomSlug = slugifyValue(`tag-${(Date.now()).toString(36)}`);
  return randomSlug || "tag";
};

const STATUS_PRIORITY = {
  pending: 0,
  approved: 1,
  rejected: 2,
};

const STATUS_META = {
  pending: { label: "Pending Review", color: "gold" },
  approved: { label: "Approved", color: "green" },
  rejected: { label: "Rejected", color: "red" },
};

const STATUS_OPTIONS = [
  { value: "approved", label: "Approved" },
  { value: "pending", label: "Pending" },
  { value: "rejected", label: "Rejected" },
];

const getStatusMeta = (status) => STATUS_META[status] || { label: "Unknown", color: "default" };

const sortTags = (items) => {
  return [...items].sort((a, b) => {
    const leftStatus = STATUS_PRIORITY[a.status] ?? 99;
    const rightStatus = STATUS_PRIORITY[b.status] ?? 99;
    if (leftStatus !== rightStatus) {
      return leftStatus - rightStatus;
    }
    const leftName = a.name || "";
    const rightName = b.name || "";
    return leftName.localeCompare(rightName, undefined, { sensitivity: "base" });
  });
};

const TagManager = ({ className = "", showHeader = true }) => {
  const { isAdmin } = useCommunityAuth();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [rowAction, setRowAction] = useState(null);
  const [filterText, setFilterText] = useState("");

  const isRowActionActive = (slug, kind) =>
    Boolean(rowAction && rowAction.slug === slug && rowAction.kind === kind);

  const moderationStats = useMemo(() => {
    let pending = 0;
    let rejected = 0;
    for (const item of tags) {
      if (item?.status === "pending") {
        pending += 1;
        continue;
      }
      if (item?.status === "rejected") {
        rejected += 1;
      }
    }
    return {
      pending,
      rejected,
      unapproved: pending + rejected,
    };
  }, [tags]);

  const fetchTags = useCallback(async () => {
    if (!isAdmin) {
      return;
    }
    setLoading(true);
    try {
      const response = await communityAPI.tags.getAll({ page_size: 200 });
      const payload = normalizeList(response.data);
      setTags(sortTags(payload));
    } catch (err) {
      console.error("Failed to load tags", err);
      message.error("Unable to load tags right now.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    if (editingTag) {
      editForm.setFieldsValue({
        name: editingTag.name,
        slug: editingTag.slug,
        description: editingTag.description || "",
        status: editingTag.status || "pending",
        moderation_notes: editingTag.moderation_notes || "",
      });
    } else {
      editForm.resetFields();
    }
  }, [editingTag, editForm]);

  const filteredTags = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) {
      return tags;
    }
    return tags.filter((item) => {
      const nameMatch = item.name?.toLowerCase().includes(query);
      const slugMatch = item.slug?.toLowerCase().includes(query);
      return nameMatch || slugMatch;
    });
  }, [filterText, tags]);

  const handleSubmit = async (values) => {
    const name = values.name?.trim();
    if (!name) {
      message.error("Tag name is required.");
      return;
    }
    setSubmitting(true);
    const payload = {
      name,
      slug: buildSlug(name, values.slug),
      description: values.description?.trim() || "",
    };
    try {
      const response = await communityAPI.tags.create(payload);
      const created = response.data;
      if (created.status === "pending") {
        message.success(`Tag "${created.name}" submitted for moderator review.`);
      } else {
        message.success(`Tag "${created.name}" created.`);
      }
      form.resetFields();
      setTags((prev) =>
        sortTags([
          { ...created },
          ...prev.filter((item) => item.id !== created.id),
        ])
      );
    } catch (err) {
      console.error("Failed to create tag", err);
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.name ||
        err.response?.data?.slug ||
        "Failed to create tag.";
      if (Array.isArray(detail)) {
        message.error(detail.join(" "));
      } else {
        message.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (tag) => {
    setEditingTag(tag);
  };

  const handleModalCancel = () => {
    setEditingTag(null);
  };

  const handleUpdate = async (values) => {
    if (!editingTag) {
      return;
    }
    const name = values.name?.trim();
    if (!name) {
      message.error("Tag name is required.");
      return;
    }
    setModalLoading(true);
    const payload = {
      name,
      slug: buildSlug(name, values.slug, editingTag.slug),
      description: values.description?.trim() || "",
      status: values.status,
      moderation_notes: values.moderation_notes?.trim() || "",
    };
    try {
      const response = await communityAPI.tags.update(editingTag.slug, payload);
      const updated = response.data;
      message.success(`Tag "${updated.name}" updated.`);
      setTags((prev) =>
        sortTags(
          prev.map((item) =>
            item.id === updated.id ? { ...item, ...updated } : item
          )
        )
      );
      setEditingTag(null);
    } catch (err) {
      console.error("Failed to update tag", err);
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.name ||
        err.response?.data?.slug ||
        "Failed to update tag.";
      if (Array.isArray(detail)) {
        message.error(detail.join(" "));
      } else {
        message.error(detail);
      }
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (tag) => {
    setRowAction({ slug: tag.slug, kind: "delete" });
    try {
      await communityAPI.tags.delete(tag.slug);
      message.success(`Tag "${tag.name}" removed.`);
      setTags((prev) => prev.filter((item) => item.id !== tag.id));
    } catch (err) {
      console.error("Failed to delete tag", err);
      const detail = err.response?.data?.detail || "Failed to delete tag.";
      if (Array.isArray(detail)) {
        message.error(detail.join(" "));
      } else {
        message.error(detail);
      }
    } finally {
      setRowAction(null);
    }
  };

  const handleApprove = async (tag) => {
    setRowAction({ slug: tag.slug, kind: "approve" });
    try {
      const { data } = await communityAPI.tags.approve(tag.slug);
      message.success(`Tag "${data.name}" approved.`);
      setTags((prev) =>
        sortTags(prev.map((item) => (item.id === data.id ? { ...item, ...data } : item)))
      );
    } catch (err) {
      console.error("Failed to approve tag", err);
      const detail = err.response?.data?.detail || "Failed to approve tag.";
      if (Array.isArray(detail)) {
        message.error(detail.join(" "));
      } else {
        message.error(detail);
      }
    } finally {
      setRowAction(null);
    }
  };

  const handleRestore = async (tag) => {
    setRowAction({ slug: tag.slug, kind: "restore" });
    try {
      const { data } = await communityAPI.tags.restore(tag.slug);
      message.success(`Tag "${data.name}" moved back to review.`);
      setTags((prev) =>
        sortTags(prev.map((item) => (item.id === data.id ? { ...item, ...data } : item)))
      );
    } catch (err) {
      console.error("Failed to restore tag", err);
      const detail = err.response?.data?.detail || "Failed to restore tag.";
      if (Array.isArray(detail)) {
        message.error(detail.join(" "));
      } else {
        message.error(detail);
      }
    } finally {
      setRowAction(null);
    }
  };

  if (!isAdmin) {
    return null;
  }

  const renderHeader = () => (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
        <Tags className="h-5 w-5" />
        Manage Tags
        <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
          ({moderationStats.unapproved} unapproved)
        </span>
      </div>
      <Button
        size="small"
        type="text"
        icon={<RefreshCw className="h-4 w-4" />}
        onClick={fetchTags}
        loading={loading}
      >
        Refresh
      </Button>
    </div>
  );

  return (
    <Card
      className={`relative overflow-hidden rounded-3xl border border-gray-200/70 dark:border-gray-700/70 bg-white/80 dark:bg-gray-900/80 backdrop-blur shadow-lg [&.ant-card]:dark:bg-gray-900 [&_.ant-card-body]:dark:bg-gray-900 [&_.ant-card-head]:dark:bg-gray-900 [&_.ant-card-head]:dark:border-gray-700 [&_.ant-card-head-title]:dark:text-white ${className}`.trim()}
      title={showHeader ? renderHeader() : null}
    >
      {!showHeader && <div className="mb-4">{renderHeader()}</div>}
      <div className="grid gap-6 md:grid-cols-2">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
          disabled={submitting}
          className="rounded-2xl border border-dashed border-indigo-200 dark:border-indigo-600/50 bg-indigo-50/60 dark:bg-indigo-950/30 p-5 [&_.ant-input]:dark:bg-gray-800 [&_.ant-input]:dark:border-gray-700 [&_.ant-input]:dark:text-white [&_.ant-input]:dark:placeholder-gray-400 [&_.ant-form-item-extra]:dark:text-gray-500 [&_.ant-input-affix-wrapper]:dark:bg-gray-800 [&_.ant-input-affix-wrapper]:dark:border-gray-700"
        >
          <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-indigo-500 dark:text-indigo-300 mb-4">
            <PlusCircle className="h-4 w-4" />
            Create New Tag
          </div>
          <Form.Item
            name="name"
            label={<span className="font-semibold dark:text-gray-200">Name</span>}
            rules={[{ required: true, message: "Provide a tag name." }]}
          >
            <Input placeholder="e.g. incident-response" maxLength={50} allowClear />
          </Form.Item>
          <Form.Item
            name="slug"
            label={<span className="font-semibold dark:text-gray-200">Slug</span>}
            extra="Optional. Leave blank to auto-generate from the name."
          >
            <Input placeholder="incident-response" maxLength={150} allowClear />
          </Form.Item>
          <Form.Item
            name="description"
            label={<span className="font-semibold dark:text-gray-200">Description</span>}
            extra="Optional. Provide a brief purpose so others understand when to reuse the tag."
          >
            <Input.TextArea rows={2} placeholder="Short guidance for this tag" maxLength={200} allowClear />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 font-semibold"
          >
            <PlusCircle className="h-4 w-4" />
            Add Tag
          </Button>
        </Form>

        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-950/70 p-5">
          <Space direction="vertical" size="large" className="w-full">
            <Input.Search
              allowClear
              placeholder="Filter by name or slug"
              value={filterText}
              onChange={(event) => setFilterText(event.target.value)}
              onSearch={(value) => setFilterText(value)}
              className="rounded-lg [&_.ant-input]:dark:bg-gray-800 [&_.ant-input]:dark:border-gray-700 [&_.ant-input]:dark:text-white [&_.ant-input]:dark:placeholder-gray-400 [&_.ant-input-group-addon]:dark:bg-gray-700 [&_.ant-input-group-addon]:dark:border-gray-700 [&_.ant-btn]:dark:bg-primary [&_.ant-btn]:dark:border-primary [&_.ant-btn]:dark:text-white [&_.ant-input-affix-wrapper]:dark:bg-gray-800 [&_.ant-input-affix-wrapper]:dark:border-gray-700 [&_.ant-input-affix-wrapper]:dark:text-white [&_.ant-input-clear-icon]:dark:text-gray-400"
            />
            {loading && tags.length === 0 ? (
              <div className="space-y-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={`tag-skeleton-${index}`} active paragraph={{ rows: 1 }} />
                ))}
              </div>
            ) : filteredTags.length === 0 ? (
              <Empty description={filterText ? "No tags match your search." : "No tags yet. Create the first one."} />
            ) : (
              <div className="max-h-80 overflow-y-auto pr-1">
                <List
                  itemLayout="vertical"
                  dataSource={filteredTags}
                  loading={loading}
                  renderItem={(item) => {
                    const statusMeta = getStatusMeta(item.status);
                    const usageValue =
                      typeof item.total_usage === "number"
                        ? item.total_usage
                        : typeof item.post_count === "number"
                        ? item.post_count
                        : undefined;
                    return (
                      <List.Item
                        key={item.id || item.slug || item.name}
                        className="px-0"
                        actions={[
                          ...(item.status === "pending"
                            ? [
                                <Button
                                  key="approve"
                                  size="small"
                                  type="primary"
                                  icon={<ShieldCheck className="h-4 w-4" />}
                                  loading={isRowActionActive(item.slug, "approve")}
                                  onClick={() => handleApprove(item)}
                                >
                                  Approve
                                </Button>,
                              ]
                            : []),
                          ...(item.status === "rejected"
                            ? [
                                <Button
                                  key="restore"
                                  size="small"
                                  type="default"
                                  icon={<RotateCcw className="h-4 w-4" />}
                                  loading={isRowActionActive(item.slug, "restore")}
                                  onClick={() => handleRestore(item)}
                                >
                                  Restore
                                </Button>,
                              ]
                            : []),
                          <Button
                            key="edit"
                            size="small"
                            type="text"
                            icon={<Edit3 className="h-4 w-4" />}
                            onClick={() => handleEdit(item)}
                          >
                            Edit
                          </Button>,
                          <Popconfirm
                            key="delete"
                            title="Delete tag"
                            description="This will remove the tag from new content, but existing posts retain their association."
                            okText="Delete"
                            okType="danger"
                            onConfirm={() => handleDelete(item)}
                          >
                            <Button
                              size="small"
                              danger
                              type="text"
                              icon={<Trash2 className="h-4 w-4" />}
                              loading={isRowActionActive(item.slug, "delete")}
                            >
                              Delete
                            </Button>
                          </Popconfirm>,
                        ]}
                      >
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                                {item.name}
                              </span>
                              <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
                            </div>
                            {typeof usageValue === "number" && (
                              <span className="text-xs font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-300">
                                {usageValue} uses
                              </span>
                            )}
                          </div>
                          {item.description && (
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {item.description}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-4 text-xs text-gray-400 dark:text-gray-500">
                            <span>Slug: {item.slug}</span>
                            {typeof item.post_count === "number" && (
                              <span>Posts: {item.post_count}</span>
                            )}
                            {typeof item.challenge_count === "number" && (
                              <span>Challenges: {item.challenge_count}</span>
                            )}
                            {item.created_by?.username && (
                              <span>Created by {item.created_by.username}</span>
                            )}
                            {item.status === "approved" && item.approved_by?.username && (
                              <span>Approved by {item.approved_by.username}</span>
                            )}
                            {item.moderation_notes && (
                              <span className="text-amber-600 dark:text-amber-300">
                                Notes: {item.moderation_notes}
                              </span>
                            )}
                          </div>
                        </div>
                      </List.Item>
                    );
                  }}
                />
              </div>
            )}
          </Space>
        </div>
      </div>

      <Modal
        title={`Edit Tag${editingTag ? `: ${editingTag.name}` : ""}`}
        open={Boolean(editingTag)}
        onCancel={handleModalCancel}
        onOk={() => editForm.submit()}
        confirmLoading={modalLoading}
        destroyOnHidden
        okText="Save changes"
        className="[&_.ant-modal-content]:dark:bg-gray-900 [&_.ant-modal-header]:dark:bg-gray-900 [&_.ant-modal-header]:dark:border-gray-700 [&_.ant-modal-title]:dark:text-white [&_.ant-modal-close]:dark:text-gray-400 [&_.ant-modal-close:hover]:dark:text-white [&_.ant-modal-footer]:dark:bg-gray-900 [&_.ant-modal-footer]:dark:border-gray-700"
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdate}
          requiredMark={false}
          className="[&_.ant-form-item-label>label]:dark:text-gray-200 [&_.ant-input]:dark:bg-gray-800 [&_.ant-input]:dark:border-gray-700 [&_.ant-input]:dark:text-white [&_.ant-input]:dark:placeholder-gray-400 [&_.ant-select-selector]:dark:bg-gray-800 [&_.ant-select-selector]:dark:border-gray-700 [&_.ant-select-selection-item]:dark:text-white [&_.ant-form-item-extra]:dark:text-gray-500 [&_.ant-input-affix-wrapper]:dark:bg-gray-800 [&_.ant-input-affix-wrapper]:dark:border-gray-700"
        >
          <Form.Item
            name="name"
            label={<span className="font-semibold">Name</span>}
            rules={[{ required: true, message: "Provide a tag name." }]}
          >
            <Input maxLength={50} allowClear placeholder="Tag name" />
          </Form.Item>
          <Form.Item
            name="slug"
            label={<span className="font-semibold">Slug</span>}
            extra="Optional. Leave blank to auto-generate from the name."
          >
            <Input maxLength={150} allowClear placeholder="custom-slug" />
          </Form.Item>
          <Form.Item
            name="description"
            label={<span className="font-semibold">Description</span>}
          >
            <Input.TextArea rows={3} maxLength={200} placeholder="Explain when to use this tag" />
          </Form.Item>
          <Form.Item
            name="status"
            label={<span className="font-semibold">Status</span>}
            rules={[{ required: true, message: "Select a status." }]}
          >
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="moderation_notes"
            label={<span className="font-semibold">Moderation Notes</span>}
            extra="Visible to other moderators and trusted reviewers."
          >
            <Input.TextArea rows={3} maxLength={300} placeholder="Internal notes or reasoning" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

TagManager.propTypes = {
  className: PropTypes.string,
  showHeader: PropTypes.bool,
};

export default TagManager;
